/**
 * The "Start 14-day trial" popup: step 1 (plan + account) happens in the
 * popup, step 2 is Stripe, reached through /pricing?plan=X&interval=Y, which
 * resumes checkout for a signed-in visitor (see utils/checkoutPlan.js).
 *
 * Company and phone are asked so new trials can be called for onboarding.
 * Google sign-up can't carry them through the redirect, so they wait in
 * localStorage and are saved to the profile once the account exists.
 */
import { isCheckoutPlan, planQuery } from './checkoutPlan';

export const DEFAULT_TRIAL_PLAN = 'professional';
export const SIGNUP_EXTRAS_KEY = 'canira_signup_extras';

/** Where the popup sends someone after step 1: Stripe, via the pricing page. */
export function checkoutPath(planKey, interval) {
  const plan = isCheckoutPlan(planKey) ? planKey : DEFAULT_TRIAL_PLAN;
  return `/pricing?${planQuery(plan, interval)}`;
}

/** Same rule as the backend: 8+ characters, an upper- and lowercase letter, a number. */
export function passwordNeeds(password = '') {
  const needs = [];
  if (password.length < 8) needs.push('8+ characters');
  if (!/[A-Z]/.test(password)) needs.push('an uppercase letter');
  if (!/[a-z]/.test(password)) needs.push('a lowercase letter');
  if (!/[0-9]/.test(password)) needs.push('a number');
  return needs;
}

/** A phone we could call: at least 7 digits, at most 20 characters. */
export function isCallablePhone(phone = '') {
  const digits = String(phone).replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15 && String(phone).trim().length <= 20;
}

/**
 * Field errors for step 1. `withPassword` is false for Google sign-up, which
 * still needs a phone.
 */
export function signupErrors(form, { withPassword = true } = {}) {
  const errors = {};
  if (withPassword) {
    if (!form.firstName?.trim()) errors.firstName = 'First name is required';
    if (!form.lastName?.trim()) errors.lastName = 'Last name is required';
    if (!form.email?.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email';
    const needs = passwordNeeds(form.password);
    if (needs.length) errors.password = `Needs ${needs.join(', ')}`;
  }
  // Company is optional.
  if ((form.company || '').trim().length > 100) errors.company = 'Keep it under 100 characters';
  if (!form.phone?.trim()) errors.phone = 'Phone is required';
  else if (!isCallablePhone(form.phone)) errors.phone = 'Enter a phone number we can call';
  return errors;
}

export function saveSignupExtras(extras) {
  try {
    localStorage.setItem(SIGNUP_EXTRAS_KEY, JSON.stringify({
      company: String(extras.company || '').trim().slice(0, 100),
      phone: String(extras.phone || '').trim().slice(0, 20),
      at: Date.now(),
    }));
  } catch (_) { /* private mode: they can add it in My Account */ }
}

/** The waiting extras, or null. Anything older than a day is dropped. */
export function takeSignupExtras() {
  try {
    const raw = localStorage.getItem(SIGNUP_EXTRAS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed.at || 0) > 24 * 3600 * 1000) {
      localStorage.removeItem(SIGNUP_EXTRAS_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearSignupExtras() {
  try { localStorage.removeItem(SIGNUP_EXTRAS_KEY); } catch (_) { /* ignore */ }
}
